import type { MarketView, MarketViewCreate, RefCode, TeamMarketView } from "./types";

export const POSITION_CHOICES = [
  { score: -2, label: "Strong seller" },
  { score: -1, label: "Seller" },
  { score: 0, label: "Neutral" },
  { score: 1, label: "Buyer" },
  { score: 2, label: "Strong buyer" },
] as const;

export function positionLabel(score: number | undefined): string {
  return POSITION_CHOICES.find((choice) => choice.score === score)?.label ?? "Not set";
}

export function dotPosition(score: number): string {
  return `${((score + 2) / 4) * 100}%`;
}

export interface MarketFactor {
  code: string;
  name: string;
  hint: string;
}

export function marketFactorsFromCodes(codes: RefCode[]): MarketFactor[] {
  return codes
    .filter((refCode) => refCode.code)
    .sort((first, second) => (first.sortOrder ?? 0) - (second.sortOrder ?? 0))
    .map((refCode) => ({
      code: refCode.code ?? "",
      name: refCode.displayName ?? refCode.code ?? "",
      hint: refCode.notes ?? "",
    }));
}

export type FactorPositions = Record<string, number>;
export type FactorNotes = Record<string, string>;

export function positionsOf(view: MarketView | null | undefined): FactorPositions {
  return Object.fromEntries((view?.factors ?? []).map((factor) => [factor.factorCode, factor.positionScore]));
}

export function notesOf(view: MarketView | null | undefined): FactorNotes {
  return Object.fromEntries(
    (view?.factors ?? []).filter((factor) => factor.note).map((factor) => [factor.factorCode, factor.note ?? ""]),
  );
}

export type VerdictLean = "Buyer leaning" | "Seller leaning" | "Balanced";

export interface Verdict {
  lean: VerdictLean;
  buyerCount: number;
  sellerCount: number;
  setCount: number;
}

export function pressureVerdict(positions: FactorPositions): Verdict | null {
  const scores = Object.values(positions);
  if (scores.length === 0) return null;
  const buyerCount = scores.filter((score) => score > 0).length;
  const sellerCount = scores.filter((score) => score < 0).length;
  const lean: VerdictLean =
    buyerCount > sellerCount ? "Buyer leaning" : sellerCount > buyerCount ? "Seller leaning" : "Balanced";
  return { lean, buyerCount, sellerCount, setCount: scores.length };
}

export type ChangeDirection = "towardBuyers" | "towardSellers" | "newlySet" | "cleared";

export interface FactorChange {
  factor: MarketFactor;
  previousScore: number | undefined;
  currentScore: number | undefined;
  direction: ChangeDirection;
  note: string | null;
}

function changeDirection(previousScore: number | undefined, currentScore: number | undefined): ChangeDirection {
  if (currentScore === undefined) return "cleared";
  if (previousScore === undefined) return "newlySet";
  return currentScore > previousScore ? "towardBuyers" : "towardSellers";
}

export function factorChanges(factors: MarketFactor[], current: MarketView, previous: MarketView): FactorChange[] {
  const currentPositions = positionsOf(current);
  const previousPositions = positionsOf(previous);
  const currentNotes = notesOf(current);
  return factors
    .filter((factor) => currentPositions[factor.code] !== previousPositions[factor.code])
    .map((factor) => {
      const currentScore = currentPositions[factor.code];
      return {
        factor,
        previousScore: previousPositions[factor.code],
        currentScore,
        direction: changeDirection(previousPositions[factor.code], currentScore),
        note: currentScore === undefined ? null : (currentNotes[factor.code] ?? null),
      };
    });
}

export interface TeamConsultantPosition {
  appUserId: number;
  name: string;
  note: string | null;
}

export interface TeamPositionGroup {
  score: number;
  consultants: TeamConsultantPosition[];
}

export interface TeamFactorSummary {
  factor: MarketFactor;
  averageScore: number | undefined;
  buyerCount: number;
  neutralCount: number;
  sellerCount: number;
  groups: TeamPositionGroup[];
}

export function teamFactorSummaries(factors: MarketFactor[], views: TeamMarketView[]): TeamFactorSummary[] {
  return factors.map((factor) => {
    const groupsByScore = new Map<number, TeamConsultantPosition[]>();
    for (const view of views) {
      const factorPosition = view.factors.find((viewFactor) => viewFactor.factorCode === factor.code);
      if (!factorPosition) continue;
      const consultantsAtScore = groupsByScore.get(factorPosition.positionScore) ?? [];
      consultantsAtScore.push({
        appUserId: view.enteredByAppUserId,
        name: view.enteredByDisplayName,
        note: factorPosition.note ?? null,
      });
      groupsByScore.set(factorPosition.positionScore, consultantsAtScore);
    }
    const groups = [...groupsByScore.entries()]
      .sort(([firstScore], [secondScore]) => firstScore - secondScore)
      .map(([score, consultants]) => ({ score, consultants }));
    const countAt = (isCounted: (score: number) => boolean) =>
      groups.filter((group) => isCounted(group.score)).reduce((total, group) => total + group.consultants.length, 0);
    const setCount = countAt(() => true);
    const scoreTotal = groups.reduce((total, group) => total + group.score * group.consultants.length, 0);
    return {
      factor,
      averageScore: setCount === 0 ? undefined : scoreTotal / setCount,
      buyerCount: countAt((score) => score > 0),
      neutralCount: countAt((score) => score === 0),
      sellerCount: countAt((score) => score < 0),
      groups,
    };
  });
}

export function teamAveragePositions(summaries: TeamFactorSummary[]): FactorPositions {
  return Object.fromEntries(
    summaries
      .filter((summary) => summary.averageScore !== undefined)
      .map((summary) => [summary.factor.code, summary.averageScore ?? 0]),
  );
}

export function teamCountsLabel(summary: TeamFactorSummary): string {
  return [
    [summary.buyerCount, "buyer"],
    [summary.neutralCount, "neutral"],
    [summary.sellerCount, "seller"],
  ]
    .filter(([count]) => Number(count) > 0)
    .map(([count, side]) => `${count} ${side}`)
    .join(" · ");
}

export interface MarketViewDraft {
  summary: string;
  positions: FactorPositions;
  notes: FactorNotes;
}

export function draftFromViews(
  current: MarketView | null | undefined,
  previous: MarketView | null | undefined,
): MarketViewDraft {
  if (current) {
    return { summary: current.summary ?? "", positions: positionsOf(current), notes: notesOf(current) };
  }
  return { summary: "", positions: positionsOf(previous), notes: {} };
}

export function togglePosition(positions: FactorPositions, factorCode: string, score: number): FactorPositions {
  const { [factorCode]: currentScore, ...otherPositions } = positions;
  return currentScore === score ? otherPositions : { ...otherPositions, [factorCode]: score };
}

export function toCreateRequest(
  draft: MarketViewDraft,
  factors: MarketFactor[],
  resinFamilyCode: string,
  month: string,
): MarketViewCreate {
  return {
    resinFamilyCode,
    month: `${month}-01`,
    summary: draft.summary.trim() || null,
    factors: factors
      .filter((factor) => draft.positions[factor.code] !== undefined)
      .map((factor) => ({
        factorCode: factor.code,
        positionScore: draft.positions[factor.code],
        note: draft.notes[factor.code]?.trim() || null,
      })),
  };
}
